export interface UserData {
    uuid: string
    email: string
    full_name: string
    timezone: string
    is_active: boolean
    is_verified: boolean
    email_verified_at: string | null
    last_login_at: string | null
    created: string
    updated: string
    organization: {
        id: string // Maps to uuid in backend
        name: string
    } | null
    role: {
        role: string
        display_name: string
        scope_department: string | null
        permissions: string[] // Permissions now come directly in role object
    } | null
    employee: {
        id: string
        department: string | null
        job_title: string | null
        is_people_manager: boolean | null
        reports_to_id: string | null
    } | null
    profile: {
        preferences: Record<string, unknown>
        profile_image_url: string | null
        onboarding_completed: boolean
    }
}

export interface OrganizationData {
    uuid: string
    name: string
    domain?: string
}

export interface AuthResponse {
    access_token: string
    refresh_token: string
    expires_at: string
    refresh_expires_at: string
    user: UserData
    organization?: OrganizationData
}

export interface CheckEmailRequest {
    email: string
}

export interface CheckEmailResponse {
    action: 'magic_link_sent' | 'redirect_to_sso' | 'user_not_found'
    message: string
    sso_url?: string
    organization_id?: string
    organization_name?: string
    sso_provider?: string
    sso_provider_name?: string
}

export interface ExchangeTokenRequest {
    token: string
}

export interface ExchangeTokenResponse {
    access_token: string
    refresh_token: string
    expires_at: string
    refresh_expires_at: string
    user: UserData
}

export interface AuthState {
    user: UserData | null
    organization: OrganizationData | null
    permissions: string[]
    ability: unknown | null // CASL ability instance
    isAuthenticated: boolean
    isLoading: boolean
    error: string | null
    isMockAuth?: boolean // Track if using mock authentication
}

/**
 * User roles matching Django4Lyfe backend SystemRole enum
 * Hierarchical order from highest (0) to lowest (6) privilege level
 */
export enum UserRole {
    // Highest privilege - Organization ownership and billing
    OWNER = 'owner', // Level 0: Original org creator, can transfer ownership

    // Administrative roles - Full system access
    ADMIN = 'admin', // Level 1: Full control except ownership management

    // HR roles - Employee data and HR system management
    HR_ADMIN = 'hr_admin', // Level 2: Full HR system access, employee data management
    HR_MANAGER = 'hr_manager', // Level 3: Department-level HR access, limited system config

    // Management roles - Team and people management
    PEOPLE_MANAGER = 'people_manager', // Level 4: Direct reports access, team analytics

    // Standard access roles
    EMPLOYEE = 'employee', // Level 5: Self-service access, basic features
    GUEST = 'guest', // Level 6: Read-only access, no data modification
}

export interface TokenData {
    access_token: string
    refresh_token: string
    expires_at: string
    refresh_expires_at: string
}

export interface AuthError {
    message: string
    code?: string
    details?: Record<string, unknown>
}
